// Resume43.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  IconButton,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

// Fixed A4 pixel dimensions (same as Resume1)
const PAGE_SIZES = {
  A4:        { width: 794,  height: 1123 },
  Letter:    { width: 816,  height: 1056 },
  Legal:     { width: 816,  height: 1344 },
  A3:        { width: 1123, height: 1587 },
  Executive: { width: 696,  height: 1010 },
};

const Resume43 = ({
  color = "#004d40",
  pageSize = "A4",
  exporting = false,
}) => {
  const page = PAGE_SIZES[pageSize] || PAGE_SIZES.A4;

  // ── Pagination state (same as Resume1) ──────────────────────────────────
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages,  setTotalPages]  = useState(1);
  const allPagesRef = useRef(null);

  // Measure content height after render and compute total pages
  useEffect(() => {
    if (!allPagesRef.current) return;
    const contentHeight = allPagesRef.current.scrollHeight;
    const pages = Math.max(1, Math.ceil(contentHeight / page.height));
    setTotalPages(pages);
  }, [page.height, color]);

  // ── Page box style (same dimensions as Resume1) ─────────────────────────
  const pageBoxSx = {
    width:     `${page.width}px`,
    height:    `${page.height}px`,
    maxWidth:  `${page.width}px`,
    overflow:  'hidden',
    bgcolor:   'white',
    boxShadow: exporting ? 'none' : '0 4px 20px rgba(0,0,0,0.15)',
    mx:        'auto',
    mb:        '24px',
    boxSizing: 'border-box',
    position:  'relative',
  };

  // ── All resume content in one scrollable ref ─────────────────────────────
  // We render the full content and clip it per page using translateY
  const contentSx = {
    position:  'absolute',
    top:       0,
    left:      0,
    width:     '100%',
    transform: `translateY(-${currentPage * page.height}px)`,
  };

  return (
    <Box sx={{ position: 'relative', fontFamily: "'Georgia', 'Times New Roman', serif" }}>

   

      {/* ── THE PAGE (clipping window) ─────────────────────────────────────── */}
      <Box sx={pageBoxSx}>
        {/* Scrollable content shifted by page offset */}
        <Box ref={allPagesRef} sx={contentSx}>

          {/* ── Original Resume43 content — UNCHANGED ─────────────────────── */}
          <Box
            sx={{
              width:      `${page.width}px`,
              minHeight:  `${page.height}px`,
              bgcolor:    'white',
              p:          { xs: 5, md: 8 },
              pt:         6,
              pb:         8,
              fontFamily: "'Georgia', 'Times New Roman', serif",
              color:      '#1a1a1a',
              boxSizing:  'border-box',
            }}
          >
            {/* Name & Title */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight:    900,
                  letterSpacing: '2px',
                  color:         color,
                  fontSize:      '2.9rem',
                  lineHeight:    1.1,
                  mb:            1,
                }}
              >
                JACQUELINE THOMPSON
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontWeight:     700,
                  color:          color,
                  letterSpacing:  '1.5px',
                  fontSize:       '1.35rem',
                  textTransform:  'uppercase',
                  mb:             2,
                }}
              >
                ENGINEERING EXECUTIVE
              </Typography>

              <Typography sx={{ fontSize: '1rem', color: '#444', lineHeight: 1.6 }}>
                123 Anywhere St., Any City • 123-456-7890 • hello@reallygreatsite.com
              </Typography>
              <Typography sx={{ fontSize: '0.95rem', color: '#555', mt: 0.5 }}>
                www.reallygreatsite.com
              </Typography>
            </Box>

            <Divider sx={{ borderColor: color, borderWidth: 1, my: 3 }} />

            {/* SUMMARY */}
            <Box sx={{ mb: 5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight:    700,
                  color:         color,
                  mb:            1.5,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                SUMMARY
              </Typography>
              <Typography sx={{ lineHeight: 1.9, fontSize: '0.98rem' }}>
                Results-oriented Engineering Executive with a proven track record of optimizing project outcomes. Skilled in strategic project management and team leadership. Seeking a challenging executive role to leverage technical expertise and drive engineering excellence.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: '#888', my: 4 }} />

            {/* WORK EXPERIENCE */}
            <Box sx={{ mb: 5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight:    700,
                  color:         color,
                  mb:            2,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                WORK EXPERIENCE
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontWeight: 700, color: '#000', mb: 0.5, fontSize: '1.1rem' }}>
                  Engineering Executive, Borcelle Technologies
                </Typography>
                <Typography sx={{ color: '#555', fontStyle: 'italic', mb: 1 }}>
                  Jan 2023 – Present
                </Typography>
                <Typography component="ul" sx={{ pl: 3, lineHeight: 2, fontSize: '0.97rem' }}>
                  <li>Implemented cost-effective solutions, resulting in a 20% reduction in project expenses.</li>
                  <li>Streamlined project workflows, enhancing overall efficiency by 25%.</li>
                  <li>Led a team in successfully delivering a complex engineering project on time and within allocated budget.</li>
                </Typography>
              </Box>

         

          
            </Box>

            <Divider sx={{ borderColor: '#888', my: 4 }} />

            {/* EDUCATION */}
            <Box sx={{ mb: 5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight:    700,
                  color:         color,
                  mb:            2,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                EDUCATION
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: 700, color: '#000' }}>
                  Master of Science in Mechanical Engineering
                </Typography>
                <Typography sx={{ color: '#555', fontStyle: 'italic', mb: 0.5 }}>
                  Specialization in "Advanced Manufacturing Practices".
                </Typography>
                <Typography sx={{ color: '#555', fontStyle: 'italic', mb: 0.5 }}>
                  Thesis on "Innovations in Sustainable Engineering Practices".
                </Typography>
                <Typography sx={{ color: '#555' }}>
                  University of Engineering and Technology • Sep 2019 – Oct 2020
                </Typography>
              </Box>

            </Box>


        
          </Box>
          {/* ── End of original content ────────────────────────────────────── */}

        </Box>
      </Box>
    </Box>
  );
};

export const resumeMeta = {
  hasPhoto: false,
  columns: 1,
  colorOptions: [
    { value: "#004d40", label: "Dark Teal" },
    { value: "#d84315", label: "Fiery Orange" },
    { value: "#1a237e", label: "Indigo" },
    { value: "#33691e", label: "Dark Green" },
    { value: "#880e4f", label: "Deep Magenta" },
  ],
};

export default Resume43;